import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useMedia } from 'use-media'

import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  Box,
  mediaQueries,
  TransparentButton,
  theme,
} from '@redriverpress/system'
import {
  RichButton,
  RichDropdownButton,
  RichDropdownOptionsWrapper,
  RichColorDropdownHover,
} from './RichTextareaComponents'

export default function ColorSelector({ editor }) {
  const isMd = useMedia(mediaQueries.md)
  const [currentColorOption, setCurrentColorOption] = useState(0)
  const [selectedColor, setSelectedColor] = useState(theme.colors.black)

  const colorOptions = [
    {
      label: theme.colors.black,
      clickEvent: () => {
        setSelectedColor(theme.colors.black)
        editor
          .chain()
          .focus()
          .setColor(theme.colors.black)
          .run()
      },
    },
    {
      label: theme.colors.green900,
      clickEvent: () => {
        setSelectedColor(theme.colors.green900)
        editor
          .chain()
          .focus()
          .setColor(theme.colors.green900)
          .run()
      },
    },
    {
      label: theme.colors.red900,
      clickEvent: () => {
        setSelectedColor(theme.colors.red900)
        editor
          .chain()
          .focus()
          .setColor(theme.colors.red900)
          .run()
      },
    },
    {
      label: theme.colors.yellow900,
      clickEvent: () => {
        setSelectedColor(theme.colors.yellow900)
        editor
          .chain()
          .focus()
          .setColor(theme.colors.yellow900)
          .run()
      },
    },
    {
      label: theme.colors.blue900,
      clickEvent: () => {
        setSelectedColor(theme.colors.blue900)
        editor
          .chain()
          .focus()
          .setColor(theme.colors.blue900)
          .run()
      },
    },
  ]

  const MAX_INDEX = colorOptions.length - 1

  // This method is sequentially switching the options listed in the colorOptions.
  // That's because on mobile breakpoint, we needed to replace the dropdown
  // by a switch button, so that it solved some scrolling issues.
  const switchColor = () => {
    const nextColorOption =
      currentColorOption === MAX_INDEX ? 0 : currentColorOption + 1
    setCurrentColorOption(nextColorOption)
    colorOptions[nextColorOption].clickEvent()
  }

  useEffect(() => {
    editor.on('selectionUpdate', () => {
      const activeColor = editor.getAttributes('textStyle').color
      !!activeColor && setSelectedColor(activeColor)
    })

    return () => {
      editor.off('selectionUpdate')
    }
  }, [editor])

  return isMd ? (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <RichDropdownButton mr={1} small secondary>
          <Box
            borderRadius='circular'
            size='20px'
            backgroundColor={selectedColor}
          />
        </RichDropdownButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content asChild>
        <RichDropdownOptionsWrapper>
          {colorOptions.map(colorOption => (
            <TransparentButton
              display='flex'
              alignItems='center'
              justifyContent='center'
              my={1}
              onClick={colorOption.clickEvent}
              key={colorOption.label}
            >
              <RichColorDropdownHover borderColor={colorOption.label} />
              <Box
                position='relative'
                borderRadius='circular'
                size='20px'
                backgroundColor={colorOption.label}
              />
            </TransparentButton>
          ))}
        </RichDropdownOptionsWrapper>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  ) : (
    <RichButton mr={1} onClick={switchColor} small secondary>
      <Box
        position='relative'
        borderRadius='circular'
        width='20px'
        height='20px'
        backgroundColor={selectedColor}
      />
    </RichButton>
  )
}

ColorSelector.propTypes = {
  editor: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool])
  ).isRequired,
}
